import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import { Loader2, Pencil, StickyNote, Trash2, X } from "lucide-react";

import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from "../ui/sheet";
import { cn } from "../../lib/utils";

import toastError from "../../errors/toastError";
import api from "../../services/api";

// Notas internas: o que a equipe anota sobre o atendimento e o cliente nunca
// vê. Fica num painel lateral para não competir com a conversa.
const TicketNotes = ({ ticketId }) => {
	const [aberto, setAberto] = useState(false);
	const [notas, setNotas] = useState([]);
	const [carregando, setCarregando] = useState(false);
	const [salvando, setSalvando] = useState(false);

	const [texto, setTexto] = useState("");
	const [editando, setEditando] = useState(null);

	const carregar = useCallback(async () => {
		if (!ticketId) return;

		setCarregando(true);
		try {
			const { data } = await api.get(`/tickets/${ticketId}/notes`);
			setNotas(data || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [ticketId]);

	useEffect(() => {
		if (aberto) carregar();
	}, [aberto, carregar]);

	const salvar = async () => {
		if (!texto.trim()) return;

		setSalvando(true);
		try {
			if (editando) {
				await api.put(`/ticket-notes/${editando}`, { note: texto });
				toast.success("Nota atualizada.");
			} else {
				await api.post(`/tickets/${ticketId}/notes`, { note: texto });
				toast.success("Nota adicionada.");
			}
			setTexto("");
			setEditando(null);
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/ticket-notes/${id}`);
			toast.success("Nota excluída.");
			// Apagar a nota que está sendo editada deixaria o formulário
			// apontando para algo que não existe mais.
			if (editando === id) {
				setEditando(null);
				setTexto("");
			}
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Sheet open={aberto} onOpenChange={setAberto}>
			<SheetTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="relative h-9 w-9"
					aria-label="Notas internas"
					title="Notas internas"
				>
					<StickyNote className="h-4 w-4" />
					{notas.length > 0 && (
						<span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white">
							{notas.length}
						</span>
					)}
				</Button>
			</SheetTrigger>

			<SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
				<SheetHeader className="border-b px-4 py-3">
					<SheetTitle className="flex items-center gap-2 text-base">
						<StickyNote className="h-4 w-4 text-amber-500" />
						Notas internas
					</SheetTitle>
					<p className="text-xs text-muted-foreground">
						Só a equipe vê. O cliente nunca recebe isto.
					</p>
				</SheetHeader>

				<div className="flex-1 space-y-2 overflow-y-auto p-4">
					{carregando ? (
						<div className="flex justify-center py-10">
							<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
						</div>
					) : notas.length === 0 ? (
						<div className="py-10 text-center">
							<StickyNote className="mx-auto h-7 w-7 text-muted-foreground/40" />
							<p className="mt-2 text-sm text-muted-foreground">
								Nenhuma nota ainda. Anote o que a equipe precisa saber.
							</p>
						</div>
					) : (
						notas.map((nota) => (
							<div
								key={nota.id}
								className={cn(
									"rounded-xl border bg-amber-500/5 p-3",
									editando === nota.id && "ring-2 ring-primary/40"
								)}
							>
								<p className="whitespace-pre-line wrap-break-word text-sm">
									{nota.note}
								</p>

								<div className="mt-2 flex items-center gap-2">
									<span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground">
										{nota.user?.name || "Automação"} ·{" "}
										{format(parseISO(nota.createdAt), "dd/MM/yy HH:mm")}
									</span>
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 shrink-0"
										aria-label="Editar nota"
										onClick={() => {
											setEditando(nota.id);
											setTexto(nota.note);
										}}
									>
										<Pencil className="h-3.5 w-3.5" />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										className="h-7 w-7 shrink-0 text-destructive"
										aria-label="Excluir nota"
										onClick={() => excluir(nota.id)}
									>
										<Trash2 className="h-3.5 w-3.5" />
									</Button>
								</div>
							</div>
						))
					)}
				</div>

				<div className="space-y-2 border-t p-4">
					{editando && (
						<div className="flex items-center gap-2 text-xs text-muted-foreground">
							<Pencil className="h-3 w-3" />
							Editando uma nota
							<Button
								variant="ghost"
								size="icon"
								className="ml-auto h-6 w-6"
								aria-label="Cancelar edição"
								onClick={() => {
									setEditando(null);
									setTexto("");
								}}
							>
								<X className="h-3.5 w-3.5" />
							</Button>
						</div>
					)}

					<Textarea
						rows={3}
						placeholder="Ex.: cliente já tentou reinstalar, pediu retorno depois das 18h."
						value={texto}
						onChange={(e) => setTexto(e.target.value)}
						onKeyDown={(e) => {
							// Ctrl+Enter salva: quem anota costuma estar com as mãos no
							// teclado no meio do atendimento.
							if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) salvar();
						}}
					/>

					<Button
						className="w-full"
						disabled={!texto.trim() || salvando}
						onClick={salvar}
					>
						{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
						{editando ? "Salvar alteração" : "Adicionar nota"}
					</Button>
				</div>
			</SheetContent>
		</Sheet>
	);
};

export default TicketNotes;
